import { createWithEqualityFn } from 'zustand/traditional'
import { ApolloError } from '@apollo/client'
import reject from 'lodash/reject'
import { shallow } from 'zustand/shallow'
import { getSnackbarWithKey } from './helpers/getSnackbarWithKey/getSnackbarWithKey'
import { DEFAULT_ERROR_TEXT } from './useSnackbarsStore.consts'
import type { Snackbar, SnackbarOptions, SnackbarVariant, SnackbarBaseOptions } from './useSnackbarsStore.types'

interface SnackbarsStore {
  snackbars: Snackbar[]
  showSnackbar(variant: SnackbarVariant, message: Snackbar['message'], options?: SnackbarOptions): string
  hideSnackbar(key: string): void
  updateSnackbar(key: string, params: Partial<Omit<SnackbarBaseOptions, 'id'> & Pick<Snackbar, 'message'>>): void
  showAlert(message: Snackbar['message'], options?: SnackbarOptions): string
  showWarning(message: Snackbar['message'], options?: SnackbarOptions): string
  showInfo(message: Snackbar['message'], options?: SnackbarOptions): string
  showError(message: Snackbar['message'], options?: SnackbarOptions): string
  showApolloError(err: ApolloError, options?: SnackbarOptions): void
  showApolloOrTextError(err: ApolloError, text?: string, options?: SnackbarOptions): void
}

export const useSnackbarsStore = createWithEqualityFn<SnackbarsStore>(
  (set, get) => ({
    snackbars: [],
    showSnackbar: (variant, message, options) => {
      const snackbar = getSnackbarWithKey({ variant, message, options })

      set(({ snackbars }) => ({ snackbars: [...snackbars, snackbar] }))
      return snackbar.key
    },
    updateSnackbar: (key, { message, ...options }) => {
      const snackbars = get().snackbars
      const snackbarIndex = snackbars.findIndex((s) => s.key === key)
      const snackbar = snackbars[snackbarIndex]

      if (snackbar) {
        set(({ snackbars }) => ({
          snackbars: [
            ...snackbars.slice(0, snackbarIndex),
            {
              ...snackbar,
              options: { ...snackbar.options, ...options },
              message,
            },
            ...snackbars.slice(snackbarIndex + 1),
          ],
        }))
      }
    },
    hideSnackbar: (key) => set(({ snackbars }) => ({ snackbars: reject(snackbars, { key }) })),
    showAlert: (message, options) => get().showSnackbar('alert', message, options),
    showWarning: (message, options) => get().showSnackbar('warning', message, options),
    showInfo: (message, options) => get().showSnackbar('info', message, options),
    showError: (message, options) => {
      if (process.env.NODE_ENV === 'development') {
        console.error('[Snackbar]: ', message)
      }
      return get().showSnackbar('error', message, options)
    },
    showApolloError: (err: ApolloError, options) => {
      err.graphQLErrors.map((m) => m.message).forEach((message) => get().showError(message, options))
    },
    showApolloOrTextError: (err, text = DEFAULT_ERROR_TEXT, options = {}) => {
      const { showError } = get()

      if (err instanceof ApolloError) {
        const messages = err.graphQLErrors.map((m) => m.message)

        if (messages.length) {
          messages.forEach((message) => showError(message, options))
          return
        }
      }

      showError(text)
    },
  }),
  shallow,
)
